import React from "react";
import { createRoot } from "react-dom/client";
import { DOM_ROOT_ID } from "./constants";

import "./customtheme.scss";
import App from "./App";
import OfflineStore from "./util/OfflineStore";
import OfflineSync from "./util/OfflineSync";

// Expose utilities for console debugging
window.OfflineStore = OfflineStore;
window.OfflineSync = OfflineSync;

// After a deploy, cached HTML references old webpack chunk hashes that no
// longer exist on the CDN. Loading those chunks throws ChunkLoadError from
// several async paths (lazy imports, CSS link tags, service worker). On the
// first such error, clear the offline service worker + Cache API and
// hard-reload with a cache-busting query param so the browser fetches
// fresh HTML and chunks. sessionStorage flag prevents reload loops if
// the failure is real (404 on a current chunk).
const CHUNK_RELOAD_FLAG = "chunk-reload-attempted";

const is_chunk_load_error = (source) => {
	if (!source) {
		return false;
	}
	const name = source.name || source.error?.name;
	const message = source.message || source.error?.message || "";
	return name === "ChunkLoadError"
		|| /Loading (CSS )?chunk [\w-]+ failed/i.test(message);
};

const hard_reload_for_chunk_error = async () => {
	// Best-effort: tear down offline service worker + CacheStorage before
	// reloading. Without this, `location.reload()` can hit the SW and get
	// back the same stale HTML it served the first time.
	try {
		if ("serviceWorker" in navigator) {
			const regs = await navigator.serviceWorker.getRegistrations();
			await Promise.all(regs.map(r => r.unregister()));
		}
		if ("caches" in window) {
			const keys = await caches.keys();
			await Promise.all(keys.map(k => caches.delete(k)));
		}
	} catch {
		// Swallow — we still want the reload to happen.
	}
	const url = new URL(window.location.href);
	url.searchParams.set("_cb", String(Date.now()));
	window.location.replace(url.toString());
};

const handle_chunk_load_error = () => {
	if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
		return;
	}
	sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now()));
	hard_reload_for_chunk_error();
};

window.addEventListener("error", (event) => {
	if (is_chunk_load_error(event.error) || is_chunk_load_error(event)) {
		handle_chunk_load_error();
	}
});

// Suppress a known platform-side blob-script error that surfaces
// inside the booth iframe: PageHeader.js extracts <script> tags
// from QG's catalog `page.copy` and injects them via Blob URLs;
// the injected script's `setupCustomSearchPage` reads `lastChild`
// on a null element (DOM not yet rendered when the script runs).
// React's dev overlay catches it as uncaught and shows the red
// modal even though it doesn't break the booth's visual search.
// Production silences the overlay; this preventDefault keeps dev
// testing clean too. Listening with capture=true so we beat the
// React error overlay's listener.
//
// Match on stack/message rather than filename — Sentry's setTimeout
// wrapper re-throws from helpers.ts so event.filename can be the
// wrapper instead of the blob URL.
const isKnownBlobScriptError = (event) => {
	const msg = event?.message || event?.error?.message || "";
	const stack = event?.error?.stack || "";
	if (!msg.includes("Cannot read properties of null (reading 'lastChild')")) return false;
	// Require the specific platform symbol in the stack. A blob: filename
	// alone isn't enough — legitimate features using URL.createObjectURL
	// (download flows, web workers, etc.) could throw the same generic
	// null-lastChild and shouldn't be silenced.
	return stack.includes("setupCustomSearchPage");
};

window.addEventListener("error", (event) => {
	if (isKnownBlobScriptError(event)) {
		console.warn("[booth] suppressing known blob-script error:", event.message);
		event.preventDefault?.();
		event.stopImmediatePropagation?.();
	}
}, true);

window.addEventListener("unhandledrejection", (event) => {
	const reason = event?.reason;
	const stack = reason?.stack || "";
	const msg = reason?.message || "";
	if (msg.includes("Cannot read properties of null (reading 'lastChild')") && stack.includes("setupCustomSearchPage")) {
		console.warn("[booth] suppressing known blob-script promise rejection:", msg);
		event.preventDefault?.();
	}
}, true);
window.addEventListener("unhandledrejection", (event) => {
	if (is_chunk_load_error(event.reason)) {
		handle_chunk_load_error();
	}
});

const container = document.getElementById(DOM_ROOT_ID || "root");

const url = new URL(window.location.href);
["nocache", "manscan"].forEach(param => {
	if (url.searchParams.has(param)) {
		url.searchParams.get(param).toLowerCase() === "false"
			? localStorage.removeItem(param)
			: localStorage.setItem(param, "true");
		url.searchParams.delete(param);
	}
});
if (url.toString() !== window.location.href) {
	window.history.pushState(null, null, url);
}

const root = createRoot(container);
root.render(
	// <React.StrictMode>
		<App tab="home" />
	// </React.StrictMode>
);

// Clear the reload flag once the session has stayed alive for a while, so a
// later deploy in the same tab can trigger a fresh reload.
setTimeout(() => sessionStorage.removeItem(CHUNK_RELOAD_FLAG), 10000);

// In dev, unregister any leftover service workers to prevent reload loops
if ("serviceWorker" in navigator && process.env.NODE_ENV !== "production") {
	navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
}

// Register offline service worker for background sync (production only — dev server conflicts with SW lifecycle)
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
	window.addEventListener("load", () => {
		navigator.serviceWorker.register("/offline-sw.js")
			.then((registration) => {
				console.warn("[ServiceWorker] Registered:", registration.scope);

				// Register for background sync if supported
				if ("sync" in registration) {
					registration.sync.register("offline-sync").catch(() => {
						// Background sync not supported or permission denied
					});
				}

				// SW message listener is managed by OfflineManager.Init()
			})
			.catch((error) => {
				console.warn("[ServiceWorker] Registration failed:", error);
			});
	});
}

